import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  CardHeader,
  Input,
} from "reactstrap";

import {
  OrganizingChapterDto,
  OrganizingLessonDto,
  UpdateLessonFeatureRequest,
} from "../../interfaces/chapter";
import DeleteModal from "../../components/Common/DeleteModal";
import { useSelector } from "react-redux";
import { PaginateQuery } from "../../interfaces";
import { NoResultFound } from "../../components/Common";
import { Paginator, TableSpinner } from "../../components";
import { RoleEnum } from "../../enums";
import { AddEditChapterModal, AddEditLessonModal } from "../Exam";
import { CopyCutLessonModal } from "./CopyCutLessonModal";
import {
  useGetOrganizingChaptersQuery,
  useDeleteChapterMutation,
  useToggleChapterStateMutation,
  useUpdateLessonFeatureMutation,
} from "../../store/api/chapterApi";
import {
  useDeleteLessonMutation,
  useToggleLessonStateMutation,
} from "../../store/api/lessonApi";
import { AccountState } from "../../store/slices/accountSlice";

type TProps = {
  subjectId: string;
};
export const OrganizingChaptersPage: React.FC<TProps> = ({ subjectId }) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(10);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  //#endregion

  //#region RTK Query hooks
  const {
    data: chaptersData,
    isLoading: chaptersLoading,
    refetch,
  } = useGetOrganizingChaptersQuery({ subjectId, data: paginateQuery });

  const [
    deleteChapter,
    { isLoading: deleteChapterLoading, error: deleteChapterError },
  ] = useDeleteChapterMutation();

  const [
    deleteLesson,
    { isLoading: deleteLessonLoading, error: deleteLessonError },
  ] = useDeleteLessonMutation();

  const [toggleChapterState] = useToggleChapterStateMutation();
  const [toggleLessonState] = useToggleLessonStateMutation();
  const [updateLessonFeature] = useUpdateLessonFeatureMutation();

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  //#endregion

  //#region internal state
  const [deletingChapterId, setDeletingChapterId] = useState<string | null>(
    null,
  );
  const [showChapterDeleteModal, setShowChapterDeleteModal] =
    useState<boolean>(false);
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [showChapterAddEditModal, setShowChapterAddEditModal] =
    useState<boolean>(false);
  const [showLessonDeleteModal, setShowLessonDeleteModal] =
    useState<boolean>(false);
  const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null);
  const [showLessonAddEditModal, setShowLessonAddEditModal] =
    useState<boolean>(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [addingLessonChapterId, setAddingLessonChapterId] = useState<
    string | null
  >(null);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "organizing" })}`;

  //#region handlers

  const onAddChapter = () => {
    setShowChapterAddEditModal(true);
  };

  const onEditChapter = (id: string) => {
    setEditingChapterId(id);
    setShowChapterAddEditModal(true);
  };

  const onToggleChapterState = async (id: string) => {
    try {
      await toggleChapterState(id).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  const onToggleLessonState = async (chapterId: string, lessonId: string) => {
    try {
      await toggleLessonState(lessonId).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  const onDeleteChapter = (id: string) => {
    setDeletingChapterId(id);
    setShowChapterDeleteModal(true);
  };

  const onDeleteChapterConfirm = async () => {
    try {
      await deleteChapter(deletingChapterId as string).unwrap();
      setDeletingChapterId(null);
      setShowChapterDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const onAddLesson = (chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setShowLessonAddEditModal(true);
  };

  const onEditLesson = (id: string, chapterId: string) => {
    setAddingLessonChapterId(chapterId);
    setEditingLessonId(id);
    setShowLessonAddEditModal(true);
  };

  const onDeleteLesson = (id: string) => {
    setDeletingLessonId(id);
    setShowLessonDeleteModal(true);
  };

  const onDeleteLessonConfirm = async () => {
    try {
      await deleteLesson(deletingLessonId as string).unwrap();
      setDeletingLessonId(null);
      setShowLessonDeleteModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  //#endregion

  const [copyingCutLesson, setCopyingCutLesson] =
    useState<OrganizingLessonDto | null>(null);
  const [showCopyCutLessonModal, setShowCopyCutLessonModal] = useState(false);
  const [lessonCopyCutAction, setLessonCopyCutAction] =
    useState<string>("copy");

  const onCopyLesson = (_lesson: OrganizingLessonDto) => {
    setCopyingCutLesson(_lesson);
    setShowCopyCutLessonModal(true);
    setLessonCopyCutAction("copy");
  };

  const onCutLesson = (_lesson: OrganizingLessonDto) => {
    setCopyingCutLesson(_lesson);
    setShowCopyCutLessonModal(true);
    setLessonCopyCutAction("cut");
  };

  const onToggleLessonFeature = async (
    chapterId: string,
    lessonId: string,
    field:
      | "enableInLecture"
      | "enableInFlashcard"
      | "enableInQuestion"
      | "isFree",
    value: boolean,
  ) => {
    let request: UpdateLessonFeatureRequest = {
      enableInFlashcard: null,
      enableInLecture: null,
      enableInQuestion: null,
      isFree: null,
    };
    if (field === "enableInLecture") request.enableInLecture = value;
    if (field === "enableInFlashcard") request.enableInFlashcard = value;
    if (field === "enableInQuestion") request.enableInQuestion = value;
    if (field === "isFree") request.isFree = value;
    try {
      await updateLessonFeature({ id: lessonId, data: request }).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("organizing")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button color="success" onClick={() => onAddChapter()}>
                <i className="ri-add-fill me-1 align-bottom"></i>
                {t("addChapter")}
              </Button>
              <Button
                color="success"
                onClick={() => {
                  setPaginateQuery({ ...paginateQuery });
                  refetch();
                }}
              >
                {t("refresh")}
              </Button>
            </div>
          </Col>
        </Row>

        {chaptersLoading && <TableSpinner />}
        {!chaptersLoading && chaptersData && (
          <Row>
            {chaptersData &&
              chaptersData.data &&
              chaptersData.data.map(
                (item: OrganizingChapterDto, key: number) => (
                  <Col lg={12} key={key}>
                    <Card>
                      <CardHeader>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex">
                            <h4>{item.sequenceNumber} - </h4>
                            &nbsp;
                            <h4>
                              {i18n.language === "ar"
                                ? item.name.ar
                                : item.name.en}{" "}
                            </h4>
                          </div>

                          <div>
                            {item.isSpecial && (
                              <span
                                className="badge"
                                style={{ backgroundColor: "gold" }}
                              >
                                {t("golden")}
                              </span>
                            )}
                            &nbsp;
                            {profile &&
                              profile.role.key == RoleEnum.SUPER_ADMIN && (
                                <Button
                                  className={`btn btn-sm ${
                                    item.isActive ? "btn-danger" : ""
                                  }`}
                                  onClick={() => onToggleChapterState(item.id)}
                                >
                                  {item.isActive
                                    ? t("deactivate")
                                    : t("activate")}
                                </Button>
                              )}
                            &nbsp;
                            <Button
                              title={t("editItem", {
                                item: "chapter",
                              })}
                              className="btn btn-sm btn-success"
                              onClick={() => onEditChapter(item.id)}
                            >
                              {t("edit")}
                            </Button>
                            &nbsp;
                            <Button
                              title={t("deleteItem", { item: "chapter" })}
                              className="btn btn-sm btn-danger"
                              onClick={() => onDeleteChapter(item.id)}
                            >
                              {t("delete")}
                            </Button>
                            &nbsp;
                            <Button
                              title={t("deleteItem", { item: "chapter" })}
                              className="btn btn-sm btn-primary"
                              onClick={() => onAddLesson(item.id)}
                            >
                              {t("addItem", { item: "lesson" })}
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardBody>
                        <Row className="g-1 mb-3">
                          <div
                            className="table-responsive"
                            style={{ maxHeight: "500px", overflowY: "auto" }}
                          >
                            <Table className="table-bordered table-nowrap align-middle mb-0">
                              <thead
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                }}
                              >
                                <tr>
                                  <th
                                    scope="col"
                                    className="text-left table-secondary"
                                  >
                                    {t("lesson")}
                                  </th>
                                  <th
                                    scope="col"
                                    className="text-center table-secondary"
                                  >
                                    {t("lecture")}
                                  </th>
                                  <th
                                    scope="col"
                                    className="text-center table-secondary"
                                  >
                                    {t("flashcard")}
                                  </th>
                                  <th
                                    scope="col"
                                    className="text-center table-secondary"
                                  >
                                    {t("question")}
                                  </th>
                                  <th
                                    scope="col"
                                    className="text-center table-secondary"
                                  >
                                    {t("freeLesson")}
                                  </th>
                                  <th
                                    scope="col"
                                    className="text-center table-secondary"
                                  >
                                    {t("action")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.lessons.map((lessonItem, lessonKey) => (
                                  <tr key={lessonKey}>
                                    <td className="fw-medium text-left">
                                      {i18n.language === "ar"
                                        ? lessonItem.name.ar
                                        : lessonItem.name.en}
                                      <span>
                                        &nbsp;
                                        <Badge color={`${"secondary"}`}>
                                          {lessonItem.sequenceNumber}
                                        </Badge>
                                      </span>
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={lessonItem.enableInLecture}
                                        onChange={(e) =>
                                          onToggleLessonFeature(
                                            item.id,
                                            lessonItem.id,
                                            "enableInLecture",
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={lessonItem.enableInFlashcard}
                                        onChange={(e) =>
                                          onToggleLessonFeature(
                                            item.id,
                                            lessonItem.id,
                                            "enableInFlashcard",
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={lessonItem.enableInQuestion}
                                        onChange={(e) =>
                                          onToggleLessonFeature(
                                            item.id,
                                            lessonItem.id,
                                            "enableInQuestion",
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="text-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={lessonItem.isFree}
                                        onChange={(e) =>
                                          onToggleLessonFeature(
                                            item.id,
                                            lessonItem.id,
                                            "isFree",
                                            e.target.checked,
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="fw-medium text-center">
                                      {profile &&
                                        profile.role.key ==
                                          RoleEnum.SUPER_ADMIN && (
                                          <Button
                                            className={`btn btn-sm ${
                                              lessonItem.isActive
                                                ? "btn-danger"
                                                : ""
                                            }`}
                                            onClick={() =>
                                              onToggleLessonState(
                                                item.id,
                                                lessonItem.id,
                                              )
                                            }
                                          >
                                            {lessonItem.isActive
                                              ? t("deactivate")
                                              : t("activate")}
                                          </Button>
                                        )}
                                      &nbsp;
                                      <Button
                                        title={t("editItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-success"
                                        onClick={() =>
                                          onEditLesson(
                                            lessonItem.id,
                                            lessonItem.id,
                                          )
                                        }
                                      >
                                        {t("edit")}
                                      </Button>
                                      &nbsp;
                                      <Button
                                        title={t("deleteItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          onDeleteLesson(lessonItem.id)
                                        }
                                      >
                                        {t("delete")}
                                      </Button>
                                      &nbsp;
                                      <Button
                                        title={t("copyItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => onCopyLesson(lessonItem)}
                                      >
                                        {t("copy")}
                                      </Button>
                                      &nbsp;
                                      <Button
                                        title={t("cutItem", {
                                          item: "lesson",
                                        })}
                                        className="btn btn-sm btn-warning"
                                        onClick={() => onCutLesson(lessonItem)}
                                      >
                                        {t("cut")}
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                ),
              )}
          </Row>
        )}
        {!chaptersLoading &&
          (!chaptersData ||
            !chaptersData.data ||
            chaptersData.data.length == 0) && <NoResultFound />}
        {!chaptersLoading &&
          chaptersData &&
          chaptersData.data &&
          chaptersData.data.length > 0 && (
            <Paginator
              itemsOnCurrentPage={chaptersData?.data?.length || 0}
              itemsPerPage={chaptersData?.meta?.itemsPerPage as number}
              totalItems={chaptersData?.meta?.totalItems || 0}
              currentPage={chaptersData?.meta?.currentPage || 1}
              totalPages={chaptersData?.meta?.totalPages || 0}
              onChangePage={(pageToChange: number) =>
                onChangePage(pageToChange)
              }
            />
          )}
        <DeleteModal
          loading={deleteChapterLoading}
          show={showChapterDeleteModal}
          onDeleteClick={() => onDeleteChapterConfirm()}
          onCloseClick={() => {
            setShowChapterDeleteModal(false);
          }}
          error={
            deleteChapterError
              ? (deleteChapterError as any)?.data?.message ||
                "An error occurred"
              : undefined
          }
        />
        <AddEditChapterModal
          subjectId={subjectId}
          id={editingChapterId}
          show={showChapterAddEditModal}
          onCloseClick={() => {
            setShowChapterAddEditModal(false);
            setEditingChapterId(null);
          }}
        />
        <DeleteModal
          loading={deleteLessonLoading}
          show={showLessonDeleteModal}
          onDeleteClick={() => onDeleteLessonConfirm()}
          onCloseClick={() => {
            setShowLessonDeleteModal(false);
          }}
          error={
            deleteLessonError
              ? (deleteLessonError as any)?.data?.message || "An error occurred"
              : undefined
          }
        />
        {addingLessonChapterId && (
          <AddEditLessonModal
            chapterId={addingLessonChapterId}
            id={editingLessonId}
            show={showLessonAddEditModal}
            onCloseClick={() => {
              setShowLessonAddEditModal(false);
              setEditingLessonId(null);
            }}
          />
        )}
        {subjectId && copyingCutLesson && (
          <CopyCutLessonModal
            subjectId={subjectId}
            lesson={copyingCutLesson}
            show={showCopyCutLessonModal}
            action={lessonCopyCutAction}
            onCloseClick={() => {
              setShowCopyCutLessonModal(false);
              setCopyingCutLesson(null);
            }}
          />
        )}
      </Container>
    </React.Fragment>
  );
};

function SimpleBadge(props: any) {
  const { classes } = props;
  return (
    <div>
      <Badge
        classes={{ badge: { backgroundColor: "#00AFD7", color: "white" } }}
        className={classes.margin}
        badgeContent={10}
      ></Badge>
    </div>
  );
}
